import {MILLISECONDS_IN_SECOND, MINUTES_IN_DAY, ONE, SECONDS_IN_MINUTE, CLASS_NAME_INVISIBLE} from "../constants";
import {getPageData} from "../page-events";
import axios from "../../axios-instance";
import {ReportLoader} from "./report-loader";
import {StepManager} from "./step-manager";
import {ReportPreview} from "./report-preview";
import {createFormData} from "../helpers/form-data-helper";
import {
  DAYS_IN_WEEK,
  FIRST_QUARTER_FIRST_MONTH, FIRST_QUARTER_LAST_MONTH,
  SECOND_QUARTER_FIRST_MONTH, SECOND_QUARTER_LAST_MONTH,
  THIRD_QUARTER_FIRST_MONTH, THIRD_QUARTER_LAST_MONTH,
  FOURTH_QUARTER_FIRST_MONTH, FOURTH_QUARTER_LAST_MONTH
} from "../../../js/modules/constants";

const DEFAULT_BANDWIDTH_ESTIMATE_KEY = 'defaultBandwidthEstimate'

class ReportBuilder {
  constructor(reportDataName) {
    this.pageData = getPageData(reportDataName);
    this.reportId = this.pageData['reportId'];
    this.orgSearchSelectId = this.pageData['orgSelectId'];
    this.clientSearchSelectId = this.pageData['clientSelectId'];
    this.dateRangeId = this.pageData['dateRangeId'];
    this.startDatepickerId = this.pageData['startDatepickerId'];
    this.endDatepickerId = this.pageData['endDatepickerId'];
    this.resellerId = this.pageData['resellerId'];
    this.scheduleRadioGroupId = this.pageData['scheduleRadioGroupId'];
    this.scheduleRadioGroupOptions = this.pageData['scheduleRadioGroupOptions'];
    this.scheduleFrequencyId = this.pageData['scheduleFrequencyId'];
    this.scheduleFrequencyOptions = this.pageData['scheduleFrequencyOptions'];
    this.scheduleAlertId = this.pageData['scheduleAlertId'];
    this.scheduleAlertValues = this.pageData['scheduleAlertValues'];
    this.scheduleToastValues = this.pageData['scheduleToastValues'];
    this.scheduleEmailError = this.pageData['scheduleEmailError'];
    this.scheduleReportNames = this.pageData['scheduleReportNames'];
    this.scheduleReportType = this.pageData['scheduleReportType'];
    this.scheduleDeliveryTypes = this.pageData['scheduleDeliveryTypes'];
    this.levelOfDetailRadioGroupId = this.pageData['levelOfDetailRadioGroupId'];
    this.radioGroupOptions = this.pageData['radioGroupOptions'];
    this.previewAlertId = this.pageData['previewAlertId'];
    this.previewReportBtnId = this.pageData['previewReportBtnId'];
    this.error_messages = this.pageData['error_messages'];
    this.serversideCurrentDate = this.pageData['serversideCurrentDate'];
    this.minimumDaysReportPeriod = 7;
    this.maximumDaysReportPeriod = 92;
    this.oneDay = MINUTES_IN_DAY * SECONDS_IN_MINUTE * MILLISECONDS_IN_SECOND;
    this.stepManager = new StepManager(this.reportId);
    this.selectedSchedule = null;
    this.selectedFrequency = null;
    this.preview = null;
    this.loader = null;
  }

  initialize() {
    const orgSearchSelect = document.getElementById(this.orgSearchSelectId);
    const clientSearchSelect = document.getElementById(this.clientSearchSelectId);
    const internetSpeedSelect = document.getElementById('executive_summary_internet_speed_select');
    const scheduleFrequencySelect = document.getElementById(this.scheduleFrequencyId);
    clientSearchSelect.disabled = true;
    if (orgSearchSelect) {
      orgSearchSelect.disabled = true;
      this.initOrgSearchSelect();
    } else {
      this.initClientSearchSelect(this.resellerId);
    }
    internetSpeedSelect.options = this.pageData['internetSpeedOptions'];
    this.disableDatePickers(this.startDatepickerId, this.endDatepickerId);
    this.setupRadioGroup(this.levelOfDetailRadioGroupId, this.radioGroupOptions);
    this.setupRadioGroup(this.scheduleRadioGroupId, this.scheduleRadioGroupOptions);
    scheduleFrequencySelect.options = this.scheduleFrequencyOptions;
    this.initScheduleChange();
    this.initFrequencyChange();
    this.checkAllFieldsFilled();
    this.initClickEvents();
    this.stepManager.showStep('options');
  }

  close() {
    if (this.loader) {
      this.loader.stopLoading();
    }
    if (this.preview) {
      this.preview.close();
    }
    this.stepManager.showStep('options');
  }

  initOrgSearchSelect() {
    const orgSearchSelect = document.getElementById(this.orgSearchSelectId);
    const requestUrl = '/async_reports/executive_summary/get_resellers_list';
    orgSearchSelect.addEventListener("axsSelectChanged", (e) => {
      const resellerId = e.detail;
      if (resellerId === "") {
        this.clearSearchSelect(this.clientSearchSelectId);
        this.disableDatePickers(this.startDatepickerId, this.endDatepickerId);
        this.checkAllFieldsFilled();
      } else {
        this.initClientSearchSelect(resellerId);
      }

    });
    this.fillSearchSelectItems(orgSearchSelect, requestUrl);
    orgSearchSelect.disabled = false;
  }

  initClientSearchSelect(resellerId) {
    const clientSearchSelect = document.getElementById(this.clientSearchSelectId);
    const requestUrl = `/async_reports/executive_summary/get_clients_list/${resellerId}`;
    this.fillSearchSelectItems(clientSearchSelect, requestUrl);
    clientSearchSelect.disabled = false;

    clientSearchSelect.addEventListener("axsSelectChanged", (e) => {
      const clientId = e.detail;
      if (clientId === "") {
        this.disableDatePickers(this.startDatepickerId, this.endDatepickerId);
        this.checkAllFieldsFilled();
      } else {
        this.setupDatePickers(
          this.startDatepickerId,
          this.endDatepickerId,
          clientId
        ).then(r => {});
      }
    });
  }

  initScheduleChange() {
    document.getElementById(this.scheduleRadioGroupId).addEventListener('change', event => {
      this.selectedSchedule = event.target.value;
      const dateRangeElement = document.getElementById(this.dateRangeId);
      const frequencyElement = document.getElementById(this.scheduleFrequencyId);
      const scheduleAlertElement = document.getElementById(this.scheduleAlertId);
      const previewAlertElement = document.getElementById(this.previewAlertId);

      if (!parseInt(this.selectedSchedule)) {
        this.removeInvisibleClass(dateRangeElement);
        this.addInvisibleClass(frequencyElement, scheduleAlertElement, previewAlertElement);
      } else {
        this.addInvisibleClass(dateRangeElement);
        this.removeInvisibleClass(frequencyElement);

        if (this.selectedFrequency) {
          this.removeInvisibleClass(scheduleAlertElement, previewAlertElement);
        }
      }
      this.checkAllFieldsFilled();
    });
  }

  initFrequencyChange() {
    const frequencyElement = document.getElementById(this.scheduleFrequencyId);
    const scheduleAlertElement = document.getElementById(this.scheduleAlertId);
    const previewAlertElement = document.getElementById(this.previewAlertId);
    const scheduleAlertContent = document.querySelector(`#${this.scheduleAlertId} .js-inline-message`);
    const previewAlertContent = document.querySelector(`#${this.previewAlertId} .js-inline-message`);

    frequencyElement.addEventListener('change', event => {
      this.selectedFrequency = event.target.value;

      if (this.selectedFrequency) {
        this.removeInvisibleClass(scheduleAlertElement, previewAlertElement);
        scheduleAlertContent.innerText = this.scheduleAlertValues[this.selectedFrequency];
        previewAlertContent.innerText = this.scheduleAlertValues[this.selectedFrequency];
      } else {
        this.addInvisibleClass(scheduleAlertElement, previewAlertElement);
        scheduleAlertContent.innerText = '';
        previewAlertContent.innerText = '';
      }
      this.checkAllFieldsFilled();
    });
  }

  initClickEvents = () => {
    document.getElementById(this.previewReportBtnId).addEventListener('click', () => {
      const reportData = this.getReportData();
      const scheduleParams = this.getScheduleParams();
      this.stepManager.showStep('preview-loading');
      axios.post('/async_reports/executive_summary/generate_report', createFormData(reportData)).then(
        response => {
          const taskReportId = response.data.report_id;
          this.loader = new ReportLoader(taskReportId);
          this.loader.processLoading().then(r => {
            const needToReloadPreview = this.preview !== null;
            if (needToReloadPreview) {
              this.preview.reloadPreviewData(
                taskReportId, reportData, scheduleParams,
                this.pageData[DEFAULT_BANDWIDTH_ESTIMATE_KEY])
            } else {
              this.preview = new ReportPreview(
                taskReportId, this.reportId, reportData, scheduleParams,
                this.pageData[DEFAULT_BANDWIDTH_ESTIMATE_KEY], this.stepManager
              );
            }
            this.preview.loadPreview().then(r => {
              this.stepManager.showStep('preview');
              if (!needToReloadPreview) {
                this.preview.initEvents(this.error_messages.cannot_load);
              }
            });
          }).catch(error => {
            if (!this.loader.stopLoad) {
              document.querySelector(`#${this.reportId} .loading`).textContent = this.error_messages.cannot_load;
            }
          });
        }
      ).catch(error => {
        document.querySelector(`#${this.reportId} .loading`).textContent = this.error_messages.cannot_load;
      });
    });
  }

  removeInvisibleClass = (...elements) => {
    elements.forEach(element => {
      if (element) {
        element.classList.remove(CLASS_NAME_INVISIBLE);
      }
    });
  }

  addInvisibleClass = (...elements) => {
    elements.forEach(element => {
      if (element) {
        element.classList.add(CLASS_NAME_INVISIBLE);
      }
    });
  }

  getReportData = () => {
    const reportDates = this.getReportDates();
    return {
      'client_id': this.getClientId(),
      'from_date': reportDates.start,
      'to_date': reportDates.end,
      'level': this.getLevelOfDetails(),
      'bandwidth_estimate': this.pageData[DEFAULT_BANDWIDTH_ESTIMATE_KEY],
    }
  }

  getScheduleParams = () => {
    return {
      selectedSchedule: this.selectedSchedule,
      selectedFrequency: this.selectedFrequency,
      scheduleAlert: this.scheduleAlertValues[this.selectedFrequency],
      scheduleEmailError: this.scheduleEmailError,
      scheduleToastValues: this.scheduleToastValues,
      reportName: this.scheduleReportNames[this.selectedFrequency],
      reportType: this.scheduleReportType,
      deliveryType: this.scheduleDeliveryTypes[this.selectedFrequency],
      level: this.getLevelOfDetails(),
      clientId: this.getClientId(),
    }
  }

  getLevelOfDetails = () => {
    return document.getElementById(this.levelOfDetailRadioGroupId).querySelector("input:checked")?.value;
  }

  getClientId = () => {
    return document.getElementById(this.clientSearchSelectId).querySelector("select")?.value;
  }

  clearSearchSelect(SearchSelectId) {
    const SearchSelect = document.getElementById(SearchSelectId);
    SearchSelect.options = [];
  }

  fillSearchSelectItems(SearchSelect, requestUrl) {
    axios.get(requestUrl).then(
      response => {
        const items = [];
        for (const item of response.data.entities_list) {
          items.push({
            value: item.id,
            label: item.name,
          });
        }
        if (items.length === ONE) {
          items[0].selected = true;
          const optionValue = items[0].value;
          const event = new CustomEvent("axsSelectChanged", {
            detail: optionValue,
          });
          SearchSelect.dispatchEvent(event);
        }
        SearchSelect.options = items;
      }
    );
  }

  async getClientDateCreated(clientId) {
    const apiUrl = `/async_reports/executive_summary/get_date_created/${clientId}`;
    const response = await axios.get(apiUrl);
    return response.data.date_created * MILLISECONDS_IN_SECOND;
  }

  formatDateForError(timestamp) {
    const date = new Date(timestamp);
    const day = date.getDate();
    const month = date.getMonth() + 1;
    const year = date.getFullYear();
    return `${month < 10 ? '0' + month : month}/${day < 10 ? '0' + day : day}/${year}`;
  }

  formatDate(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  countDaysBetweenDates(firstDate, secondDate) {
    return Math.round(Math.abs((firstDate - secondDate) / this.oneDay));
  }

  convertToDate(datetime) {
    const dd = String(datetime.getDate()).padStart(2, '0');
    const mm = String(datetime.getMonth() + 1).padStart(2, '0');
    const yyyy = datetime.getFullYear();
    return new Date(`${mm}/${dd}/${yyyy}`);
  }

  addDays(date, days) {
    const newDate = new Date(date);
    newDate.setDate(newDate.getDate() + days);
    return newDate;
  }

  addYears(date, years) {
    const newDate = new Date(date);
    newDate.setFullYear(newDate.getFullYear() + years);
    return newDate;
  }

  disableDatePickers(startDateId, endDateId) {
    const startDatePicker = document.getElementById(startDateId);
    const endDatePicker = document.getElementById(endDateId);

    startDatePicker.disabled = true;
    endDatePicker.disabled = true;

    const startDateInput = this.getDatePickerInput(startDatePicker);
    if (startDateInput) {
      startDateInput.value = '';
      startDatePicker.min = null;
      startDatePicker.max = null;
    }
    const endDateInput = this.getDatePickerInput(endDatePicker);
    if (endDateInput) {
      endDateInput.value = '';
      endDatePicker.min = null;
      endDatePicker.max = null;
    }
  }

  async setupDatePickers(startDateId, endDateId, clientId) {
    const dateCreated = await this.getClientDateCreated(clientId);
    const startDatePicker = document.getElementById(startDateId);
    const endDatePicker = document.getElementById(endDateId);

    startDatePicker.addEventListener('change', () => {
      endDatePicker.disabled = false;
      this.validatePeriod(startDatePicker, endDatePicker, dateCreated);
      this.checkAllFieldsFilled();
    });

    endDatePicker.addEventListener('change', () => {
      this.validatePeriod(startDatePicker, endDatePicker, dateCreated);
      this.checkAllFieldsFilled();
    });

    startDatePicker.disabled = false;
  }

  validatePeriod(startDatePicker, endDatePicker, createdTimestamp) {
    const startDatePickerValue = this.getDatePickerInput(startDatePicker).value;
    const endDatePickerValue = this.getDatePickerInput(endDatePicker).value;

    const startDate = startDatePickerValue ? new Date(startDatePickerValue) : null;
    const endDate = endDatePickerValue ? new Date(endDatePickerValue) : null;

    const createdDateTime = new Date(createdTimestamp);
    const createdDate = this.convertToDate(createdDateTime);

    const currentDate = new Date(this.serversideCurrentDate);

    startDatePicker?.removeAttribute('status');
    startDatePicker?.removeAttribute('message');
    endDatePicker?.removeAttribute('status');
    endDatePicker?.removeAttribute('message');

    if (startDate && startDate < createdDate) {
      const errorDate = this.formatDateForError(createdDate);
      startDatePicker.setAttribute('status', 'error');
      startDatePicker.setAttribute('message', this.error_messages.client_not_exists.replace('%s', errorDate));

    } else if (startDate && endDate && endDate < startDate ) {
      startDatePicker.setAttribute('status', 'error');
      startDatePicker.setAttribute('message', this.error_messages.start_before_end);

    } else if (endDate && this.countDaysBetweenDates(createdDate, currentDate) + 1 < this.minimumDaysReportPeriod) {
      endDatePicker.setAttribute('status', 'error');
      endDatePicker.setAttribute('message', this.error_messages.client_too_new);

    } else if (startDate && endDate && this.countDaysBetweenDates(startDate, endDate) + 1 < this.minimumDaysReportPeriod) {
      endDatePicker.setAttribute('status', 'error');
      endDatePicker.setAttribute('message', this.error_messages.range_too_small);
      startDatePicker.setAttribute('status', 'error');

    } else if (startDate && endDate && this.countDaysBetweenDates(startDate, endDate) + 1 > this.maximumDaysReportPeriod) {
      endDatePicker.setAttribute('status', 'error');
      endDatePicker.setAttribute('message', this.error_messages.range_too_big);
      startDatePicker.setAttribute('status', 'error');

    } else if (endDate && endDate >= currentDate) {
      endDatePicker.setAttribute('status', 'error');
      endDatePicker.setAttribute('message', this.error_messages.out_of_range);
    }
  }

  getDatePickerInput(datePicker) {
    return datePicker.querySelector('input');
  }

  setupRadioGroup(radioGroupId, radioGroupOptions) {
    const radioGroup = document.getElementById(radioGroupId);
    const items = [];
    for (const option of radioGroupOptions) {
      items.push({
        label: option.label,
        description: option.description,
        value: option.value,
        checked: option.checked,
      });
    }
    radioGroup.options = items;
  }

  checkAllFieldsFilled() {
    const startDatePicker = document.getElementById(this.startDatepickerId);
    const endDatePicker = document.getElementById(this.endDatepickerId);
    const orgSearchSelect = document.getElementById(this.orgSearchSelectId);
    const button = document.getElementById(this.previewReportBtnId);

    const clientSearchSelect = document.getElementById(this.clientSearchSelectId);
    const startDateInput = this.getDatePickerInput(startDatePicker);

    const endDateInput = this.getDatePickerInput(endDatePicker);

    const isStartDateFilled = startDateInput &&
      this.validateDateInputFormat(startDateInput.value) &&
      startDatePicker.getAttribute('status') !== 'error';

    const isEndDateFilled = endDateInput &&
      this.validateDateInputFormat(endDateInput.value) &&
      endDatePicker.getAttribute('status') !== 'error';

    const isOrgSelectFilled = ((orgSearchSelect && orgSearchSelect.querySelector("select")?.value) ||
      !orgSearchSelect);

    const isClientSelectFilled = clientSearchSelect.querySelector("select")?.value;
    const isFrequencySelected = parseInt(this.selectedSchedule) === 1 && this.selectedFrequency;
    const isNowSelected = parseInt(this.selectedSchedule) !== 1 && isStartDateFilled && isEndDateFilled;
    const allFieldsFilled = (isNowSelected || isFrequencySelected) && isOrgSelectFilled && isClientSelectFilled;
    button.disabled = !allFieldsFilled;
  }

  isDateWithinRange(datePicker) {
    const inputValue = new Date(this.getDatePickerInput(datePicker).value);
    const minDate = new Date(datePicker.min);
    const maxDate = new Date(datePicker.max);

    return inputValue >= minDate && inputValue <= maxDate;
  }

  validateDateInputFormat(value) {
    const simpleDateRegex = /^\d{4}-(0[1-9]|1[012])-(0[1-9]|[12][0-9]|3[01])$/;
    return simpleDateRegex.test(value)
  }

  /**
   * Gets the start and end dates of the report based on the current settings:
   * for now the dates that are set manually are returned, for a scheduled
   * report the start and end of the previous period are returned (previous
   * week, month or quarter).
   */
  getReportDates = () => {
    let schedulePeriod = {};

    if (this.selectedSchedule && this.selectedFrequency) {
      if (this.selectedFrequency === 'weekly') {
        schedulePeriod = this.getWeeklyPeriod();

      } else if (this.selectedFrequency === 'monthly') {
        schedulePeriod = this.getMonthlyPeriod();

      } else if (this.selectedFrequency === 'quarterly') {
        schedulePeriod = this.getQuarterlyPeriod();
      }

      return {
        start: this.formatScheduleDate(schedulePeriod.start),
        end: this.formatScheduleDate(schedulePeriod.end)
      };

    } else {
      return {
        start: this.getDatePickerInput(document.getElementById(this.startDatepickerId)).value,
        end: this.getDatePickerInput(document.getElementById(this.endDatepickerId)).value,
      }
    }
  }

  getWeeklyPeriod = () => {
    const today = new Date();
    const currentDayOfWeek = today.getDay();
    const daysToLastMonday = (currentDayOfWeek === 0 ? DAYS_IN_WEEK - 1 : currentDayOfWeek - 1) + DAYS_IN_WEEK;

    const startDate = new Date(today);
    startDate.setDate(today.getDate() - daysToLastMonday);

    const endDate = new Date(startDate);
    endDate.setDate(startDate.getDate() + DAYS_IN_WEEK - 1);

    return {
      start: startDate,
      end: endDate,
    };
  }

  getMonthlyPeriod = () => {
    const today = new Date();
    return {
      start: new Date(today.getFullYear(), today.getMonth() - 1, 1),
      end: new Date(today.getFullYear(), today.getMonth(), 0),
    };
  }

  getQuarterlyPeriod = () => {
    const today = new Date();
    const currentMonth = today.getMonth();
    let prevQuarterStartMonth;
    let prevQuarterYear = today.getFullYear();

    if (currentMonth >= FIRST_QUARTER_FIRST_MONTH && currentMonth <= FIRST_QUARTER_LAST_MONTH) {
      prevQuarterStartMonth = FOURTH_QUARTER_FIRST_MONTH;
      prevQuarterYear--;

    } else if (currentMonth >= SECOND_QUARTER_FIRST_MONTH && currentMonth <= SECOND_QUARTER_LAST_MONTH) {
      prevQuarterStartMonth = FIRST_QUARTER_FIRST_MONTH;

    } else if (currentMonth >= THIRD_QUARTER_FIRST_MONTH && currentMonth <= THIRD_QUARTER_LAST_MONTH) {
      prevQuarterStartMonth = SECOND_QUARTER_FIRST_MONTH;

    } else {
      prevQuarterStartMonth = THIRD_QUARTER_FIRST_MONTH;
    }

    const startDate = new Date(prevQuarterYear, prevQuarterStartMonth, 1);

    let currentQuarterStartMonth;
    if (currentMonth >= FIRST_QUARTER_FIRST_MONTH && currentMonth <= FIRST_QUARTER_LAST_MONTH) {
      currentQuarterStartMonth = FIRST_QUARTER_FIRST_MONTH;

    } else if (currentMonth >= SECOND_QUARTER_FIRST_MONTH && currentMonth <= SECOND_QUARTER_LAST_MONTH) {
      currentQuarterStartMonth = SECOND_QUARTER_FIRST_MONTH;

    } else if (currentMonth >= THIRD_QUARTER_FIRST_MONTH && currentMonth <= THIRD_QUARTER_LAST_MONTH) {
      currentQuarterStartMonth = THIRD_QUARTER_FIRST_MONTH;

    } else {
      currentQuarterStartMonth = FOURTH_QUARTER_FIRST_MONTH;
    }
    const endDate = new Date(today.getFullYear(), currentQuarterStartMonth, FIRST_QUARTER_FIRST_MONTH);

    return {
      start: startDate,
      end: endDate,
    };
  }

  /**
   * Formats a date value into YYYY-MM-DD text format
   * (used for scheduled report generation).
   */
  formatScheduleDate = (date) => {
    if (!date) {
      return null;
    }
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
}

export {ReportBuilder};
